import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Copy, Check, Share2, Download, Link as LinkIcon, MessageCircle, Mail, Eye, Gift } from 'lucide-react';
import { OrderBusinessCardsModal } from './OrderBusinessCardsModal';

/**
 * Modal allowing patients/practitioners to share a doctor profile via:
 *  - Copy link
 *  - QR code (download high-res PNG, or scan)
 *  - Visit card (download printable 1080×1920 PNG)
 *  - WhatsApp, Email, native Web Share API
 *
 * Props:
 *  - doctor: { id, first_name, last_name, specialty, city }
 *  - open: bool
 *  - onClose: () => void
 *  - apiBase: backend base URL (e.g. https://doctodays.com)
 *  - siteBase: site base URL (e.g. https://doctodays.com)
 */
export const ShareDoctorModal = ({ doctor, open, onClose, apiBase, siteBase, isOwner = false, practitionerToken = null }) => {
  const [copied, setCopied] = useState(false);
  const [qrLoading, setQrLoading] = useState(true);
  const [shareUrl, setShareUrl] = useState('');
  const [orderOpen, setOrderOpen] = useState(false);

  // Reset state when modal opens
  useEffect(() => {
    if (open) {
      setCopied(false);
      setQrLoading(true);
    }
  }, [open]);

  // Resolve the friendly slug URL once doctor is known.
  useEffect(() => {
    if (!doctor?.id || !apiBase || !siteBase) return;
    const siteOrigin = String(siteBase).replace(/\/$/, '');
    const apiOrigin = String(apiBase).replace(/\/$/, '');
    // Optimistic fallback to UUID URL, then upgrade to friendly slug.
    setShareUrl(`${siteOrigin}/doctor/${doctor.id}`);
    fetch(`${apiOrigin}/api/doctors/${doctor.id}/slug`)
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (data?.url) setShareUrl(`${siteOrigin}${data.url}`);
      })
      .catch(() => {});
  }, [doctor?.id, apiBase, siteBase]);

  if (!doctor) return null;

  const doctorName = `Dr ${doctor.first_name || ''} ${doctor.last_name || ''}`.trim();
  const qrPngUrl = `${(apiBase || '').replace(/\/$/, '')}/api/doctors/${doctor.id}/qr.png`;
  const cardPngUrl = `${(apiBase || '').replace(/\/$/, '')}/api/doctors/${doctor.id}/share-card.png`;
  // New professional 85×55 mm business card (print-ready PDF + previews)
  const bizCardPdfUrl   = `${(apiBase || '').replace(/\/$/, '')}/api/doctors/${doctor.id}/business-card.pdf`;
  const bizCardFrontUrl = `${(apiBase || '').replace(/\/$/, '')}/api/doctors/${doctor.id}/business-card-front.png`;
  const bizCardBackUrl  = `${(apiBase || '').replace(/\/$/, '')}/api/doctors/${doctor.id}/business-card-back.png`;

  const shareText = `Prenez rendez-vous avec ${doctorName}${
    doctor.specialty ? `, ${doctor.specialty}` : ''
  }${doctor.city ? ` à ${doctor.city}` : ''} sur DoctoDays.`;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // fallback for older browsers
      const ta = document.createElement('textarea');
      ta.value = shareUrl;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleNativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: doctorName + ' — DoctoDays',
          text: shareText,
          url: shareUrl,
        });
      } catch (e) {
        // user cancelled or error
      }
    } else {
      handleCopy();
    }
  };

  const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(`${shareText} ${shareUrl}`)}`;
  const mailtoUrl = `mailto:?subject=${encodeURIComponent(
    'Rendez-vous avec ' + doctorName
  )}&body=${encodeURIComponent(`${shareText}\n\n${shareUrl}`)}`;
  const smsUrl = `sms:?body=${encodeURIComponent(`${shareText} ${shareUrl}`)}`;

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-end md:items-center justify-center p-4"
          onClick={onClose}
          data-testid="share-doctor-modal"
        >
          <motion.div
            initial={{ opacity: 0, y: 50, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 50, scale: 0.95 }}
            transition={{ type: 'spring', damping: 25 }}
            className="bg-white rounded-3xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between p-5 border-b border-slate-100">
              <h2 className="text-lg font-bold text-slate-900 flex items-center gap-2">
                <Share2 className="w-5 h-5 text-emerald-600" />
                Partager ce praticien
              </h2>
              <button
                onClick={onClose}
                className="p-2 hover:bg-slate-100 rounded-xl transition-colors"
                aria-label="Fermer"
                data-testid="close-share-modal"
              >
                <X className="w-5 h-5 text-slate-500" />
              </button>
            </div>

            <div className="p-5 space-y-5">
              {/* Doctor name */}
              <div className="text-center">
                <p className="text-xl font-bold text-slate-900">{doctorName}</p>
                <p className="text-slate-500 text-sm">
                  {doctor.specialty}
                  {doctor.city ? ` · ${doctor.city}` : ''}
                </p>
              </div>

              {/* QR Code */}
              <div className="bg-gradient-to-br from-emerald-50 to-white rounded-2xl p-4 border border-emerald-100">
                <div className="relative bg-white rounded-xl shadow-sm w-48 h-48 mx-auto mb-3 overflow-hidden flex items-center justify-center">
                  {qrLoading && (
                    <div className="absolute inset-0 flex items-center justify-center text-slate-400 text-sm">
                      Chargement…
                    </div>
                  )}
                  <img
                    src={qrPngUrl}
                    alt={`Code QR pour ${doctorName}`}
                    className="w-full h-full object-contain"
                    onLoad={() => setQrLoading(false)}
                    onError={() => setQrLoading(false)}
                    data-testid="share-qr-image"
                  />
                </div>
                <p className="text-center text-xs text-slate-500 px-2">
                  Scannez ce code pour ouvrir la page du praticien
                </p>
              </div>

              {/* Copy link */}
              <div>
                <label className="text-xs uppercase font-semibold text-slate-500 mb-1.5 block">
                  Lien direct
                </label>
                <div className="flex gap-2">
                  <div className="flex-1 flex items-center gap-2 px-3 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm text-slate-700 overflow-hidden">
                    <LinkIcon className="w-4 h-4 text-slate-400 flex-shrink-0" />
                    <span className="truncate">{shareUrl}</span>
                  </div>
                  <button
                    onClick={handleCopy}
                    className={`px-3 py-2.5 rounded-xl font-semibold text-sm transition-all ${
                      copied
                        ? 'bg-emerald-600 text-white'
                        : 'bg-emerald-50 text-emerald-700 hover:bg-emerald-100 border border-emerald-200'
                    }`}
                    data-testid="copy-share-link"
                  >
                    {copied ? (
                      <span className="flex items-center gap-1">
                        <Check className="w-4 h-4" /> Copié
                      </span>
                    ) : (
                      <span className="flex items-center gap-1">
                        <Copy className="w-4 h-4" /> Copier
                      </span>
                    )}
                  </button>
                </div>
              </div>

              {/* Share buttons — wrap each <a> in a span/div so they're not
                 direct children of a grid div (avoids the Emergent badge
                 CSS rule that hides div:has(> a[href*="emergent"])). */}
              <div>
                <label className="text-xs uppercase font-semibold text-slate-500 mb-2 block">
                  Partager via
                </label>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8 }}>
                  <span>
                    <a
                      href={whatsappUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex flex-col items-center gap-1.5 p-3 rounded-xl bg-[#25D366]/10 hover:bg-[#25D366]/20 transition-colors"
                      data-testid="share-whatsapp"
                      aria-label="Partager via WhatsApp"
                    >
                      <MessageCircle className="w-5 h-5 text-[#25D366]" />
                      <span className="text-xs font-medium text-slate-700">WhatsApp</span>
                    </a>
                  </span>
                  <span>
                    <a
                      href={smsUrl}
                      className="flex flex-col items-center gap-1.5 p-3 rounded-xl bg-blue-50 hover:bg-blue-100 transition-colors"
                      data-testid="share-sms"
                      aria-label="Partager par SMS"
                    >
                      <MessageCircle className="w-5 h-5 text-blue-600" />
                      <span className="text-xs font-medium text-slate-700">SMS</span>
                    </a>
                  </span>
                  <span>
                    <a
                      href={mailtoUrl}
                      className="flex flex-col items-center gap-1.5 p-3 rounded-xl bg-amber-50 hover:bg-amber-100 transition-colors"
                      data-testid="share-email"
                      aria-label="Partager par e-mail"
                    >
                      <Mail className="w-5 h-5 text-amber-600" />
                      <span className="text-xs font-medium text-slate-700">E-mail</span>
                    </a>
                  </span>
                  <button
                    onClick={handleNativeShare}
                    className="flex flex-col items-center gap-1.5 p-3 rounded-xl bg-emerald-50 hover:bg-emerald-100 transition-colors"
                    data-testid="share-native"
                    aria-label="Plus d'options de partage"
                  >
                    <Share2 className="w-5 h-5 text-emerald-600" />
                    <span className="text-xs font-medium text-slate-700">Autres</span>
                  </button>
                </div>
              </div>

              {/* Business card — print-ready, premium medical design */}
              <div
                style={{
                  display: 'block',
                  borderTop: '1px solid #f1f5f9',
                  paddingTop: 16,
                  marginTop: 4,
                }}
                data-testid="print-section"
              >
                <p
                  style={{
                    fontSize: 11,
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    color: '#64748b',
                    marginBottom: 12,
                  }}
                >
                  Ma carte de visite professionnelle
                </p>

                {/* Visual preview of the card (front + back) */}
                <span style={{ display: 'block', marginBottom: 12 }}>
                  <span style={{ display: 'flex', gap: 8 }}>
                    <span style={{ flex: 1, display: 'block' }}>
                      <img
                        src={bizCardFrontUrl}
                        alt="Aperçu carte recto"
                        style={{
                          width: '100%',
                          borderRadius: 8,
                          border: '1px solid #e2e8f0',
                          boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
                          display: 'block',
                        }}
                        loading="lazy"
                      />
                    </span>
                    <span style={{ flex: 1, display: 'block' }}>
                      <img
                        src={bizCardBackUrl}
                        alt="Aperçu carte verso"
                        style={{
                          width: '100%',
                          borderRadius: 8,
                          border: '1px solid #e2e8f0',
                          boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
                          display: 'block',
                        }}
                        loading="lazy"
                      />
                    </span>
                  </span>
                </span>

                {/* Free gift CTA — only for the practitioner himself */}
                {isOwner && (
                  <span style={{ display: 'block', marginBottom: 8 }}>
                    <button
                      type="button"
                      onClick={() => setOrderOpen(true)}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 8,
                        width: '100%',
                        padding: '14px 16px',
                        borderRadius: 12,
                        backgroundColor: '#059669',
                        color: 'white',
                        fontSize: 15,
                        fontWeight: 700,
                        border: 'none',
                        cursor: 'pointer',
                        boxShadow: '0 4px 12px rgba(5, 150, 105, 0.25)',
                        boxSizing: 'border-box',
                      }}
                      data-testid="order-100-cards-cta"
                    >
                      <Gift className="w-4 h-4" />
                      🎁 Commander 100 cartes offertes
                    </button>
                  </span>
                )}

                {/* Download PDF (DIY printing) */}
                <span style={{ display: 'block', marginBottom: 8 }}>
                  <a
                    href={bizCardPdfUrl}
                    download={`doctodays-carte-${(doctor.last_name || 'praticien').toLowerCase()}.pdf`}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 8,
                      width: '100%',
                      padding: '12px 16px',
                      borderRadius: 12,
                      backgroundColor: isOwner ? '#f1f5f9' : '#059669',
                      color: isOwner ? '#0f172a' : 'white',
                      fontSize: 14,
                      fontWeight: 600,
                      textDecoration: 'none',
                      boxSizing: 'border-box',
                      boxShadow: isOwner ? 'none' : '0 1px 2px rgba(0,0,0,0.05)',
                    }}
                    data-testid="download-business-card-pdf"
                  >
                    <Download className="w-4 h-4" />
                    {isOwner ? 'Télécharger le PDF imprimable' : 'Télécharger la carte (PDF)'}
                  </a>
                </span>

                <span style={{ display: 'flex', gap: 8 }}>
                  <span style={{ flex: 1, display: 'block' }}>
                    <a
                      href={cardPngUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 8,
                        padding: '10px 12px',
                        borderRadius: 12,
                        backgroundColor: '#f1f5f9',
                        color: '#0f172a',
                        fontSize: 13,
                        fontWeight: 600,
                        textDecoration: 'none',
                      }}
                      data-testid="preview-poster"
                    >
                      <Eye className="w-4 h-4" />
                      Affiche A4
                    </a>
                  </span>
                  <span style={{ flex: 1, display: 'block' }}>
                    <a
                      href={qrPngUrl}
                      download={`doctodays-qr-${doctor.id.slice(0, 8)}.png`}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 8,
                        padding: '10px 12px',
                        borderRadius: 12,
                        backgroundColor: '#f1f5f9',
                        color: '#0f172a',
                        fontSize: 13,
                        fontWeight: 600,
                        textDecoration: 'none',
                      }}
                      data-testid="download-qr"
                    >
                      <Download className="w-4 h-4" />
                      QR HD
                    </a>
                  </span>
                </span>

                <p
                  style={{
                    fontSize: 12,
                    color: '#94a3b8',
                    marginTop: 12,
                    textAlign: 'center',
                  }}
                >
                  Format pro 85×55 mm — prêt-à-imprimer (Vistaprint, Helloprint…).
                </p>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}

      {/* Order modal for the 100 free cards */}
      <OrderBusinessCardsModal
        open={orderOpen}
        onClose={() => setOrderOpen(false)}
        practitioner={doctor}
        apiBase={apiBase}
        token={practitionerToken}
      />
    </AnimatePresence>
  );
};

export default ShareDoctorModal;
